import { IGetLayoutTitle } from 'src/components/stepper/assemblyStatesStepper/AssemblyStatesStepper.interface';
import { MAX_ASSEMBLY_ROTATION_DURATION_SECONDS } from 'src/constants';
import {
  type AssemblyJourneys,
  DefaultRotationValues,
  SectionJourney,
} from 'src/constants/assemblyJourneys';
import { IAssemblyConfigLayout, SectionTypes } from 'src/interfaces';
import type { GcfMockData } from 'src/utils/gcf-helper';
import { lang } from 'src/utils/language';
import { checkSectionTypes } from 'src/utils/section-types-helper';

import { AssemblySectionRotationArea } from '../assemblySectionRotationArea/AssemblySectionRotationArea';
import { AssemblyStatesSidebar } from '../assemblyStatesSidebar/AssemblyStatesSidebar';
import { AssemblySectionRotationAreaContainer } from './AssemblyEditPageSidebar.styled';

interface SectionRotationDynamicSectionsProps {
  sectionTypes?: SectionTypes[];
  setActiveAssemblyJourney: (journeyState?: AssemblyJourneys) => void;
  activeAssemblyJourney?: AssemblyJourneys;
  onUpdateRotationInterval?: (
    value: number,
    type: keyof IAssemblyConfigLayout,
  ) => void;
  rotationIntervals?: IAssemblyConfigLayout;
  disabled?: boolean;
  isOwnerOperator: boolean;
  getLayoutTitle?: IGetLayoutTitle;
  setIsCodConfirmationOpen: (value?: boolean) => void;
  setCustomerState: (value: Partial<GcfMockData>) => void;
  customerState?: Partial<GcfMockData>;
  onChangeDaypart?: (id: number) => void;
  selectedDaypartId?: number;
}

export const SectionRotationDynamicSections = (
  props: SectionRotationDynamicSectionsProps,
) => {
  const {
    sectionTypes,
    setActiveAssemblyJourney,
    activeAssemblyJourney,
    onUpdateRotationInterval,
    rotationIntervals,
    isOwnerOperator,
    disabled,
    getLayoutTitle,
    setCustomerState,
    customerState,
    setIsCodConfirmationOpen,
    onChangeDaypart,
  } = props;

  const { hasJourney, hasSectionRotation, hasTakeOver } =
    checkSectionTypes(sectionTypes);

  const getTooltipValue = (key: string) =>
    isOwnerOperator
      ? (lang.layoutRotation as Record<string, string>)[`OO${key}`]
      : (lang.layoutRotation as Record<string, string>)[key];

  const defaultLayoutValue =
    rotationIntervals?.defaultLayout?.duration ?? DefaultRotationValues.DEFAULT;
  const sectionRotationValue =
    rotationIntervals?.sectionRotation?.duration ??
    DefaultRotationValues.SECTION_TIMER;
  const takeoverValue =
    rotationIntervals?.takeover?.duration ?? DefaultRotationValues.LAYOUT_TIMER;

  const isValidDuration = (value?: number) =>
    typeof value === 'number' &&
    value > 0 &&
    value <= MAX_ASSEMBLY_ROTATION_DURATION_SECONDS;

  return [
    ...(hasJourney || hasSectionRotation || hasTakeOver
      ? [
          {
            label: getLayoutTitle
              ? getLayoutTitle(lang.defaultLayout, SectionJourney.DEFAULT)
              : lang.defaultLayout,
            content: (
              <AssemblySectionRotationAreaContainer>
                {hasJourney && (
                  <AssemblyStatesSidebar
                    setActiveAssemblyJourney={setActiveAssemblyJourney}
                    activeAssemblyJourney={activeAssemblyJourney}
                    setIsCodConfirmationOpen={setIsCodConfirmationOpen}
                    setCustomerState={setCustomerState}
                    customerState={customerState}
                    getLayoutTitle={getLayoutTitle}
                    onChangeDaypart={onChangeDaypart}
                    selectedDaypartId={props.selectedDaypartId}
                  />
                )}

                <AssemblySectionRotationArea
                  previewButtonLabel={
                    getLayoutTitle
                      ? getLayoutTitle(
                          lang.layoutRotation.defaultLayoutPreviewButtonLabel,
                          SectionJourney.DEFAULT,
                          true,
                        )
                      : lang.layoutRotation.defaultLayoutPreviewButtonLabel
                  }
                  preview={activeAssemblyJourney === SectionJourney.DEFAULT}
                  onPreviewClick={() =>
                    setActiveAssemblyJourney(SectionJourney.DEFAULT)
                  }
                  inputValue={defaultLayoutValue}
                  onInputChange={(value) =>
                    onUpdateRotationInterval &&
                    onUpdateRotationInterval(value, 'defaultLayout')
                  }
                  inputLabel={lang.layoutRotation.defaultLayoutInputLabel}
                  inputTooltip={getTooltipValue('defaultLayoutInputTooltip')}
                  disabled={disabled}
                  error={!isValidDuration(defaultLayoutValue)}
                  helperText={
                    !isValidDuration(defaultLayoutValue)
                      ? lang.layoutRotation.maxTimeErrorMessage
                      : ''
                  }
                  options={{
                    hidePreviewBtn: hasJourney,
                    hideInput: !hasSectionRotation && !hasTakeOver,
                  }}
                />
              </AssemblySectionRotationAreaContainer>
            ),
            disabledExpand: false,
            hasError: false,
            defaultExpanded: true,
          },
        ]
      : []),
    ...(hasSectionRotation
      ? [
          {
            label: getLayoutTitle
              ? getLayoutTitle(
                  lang.layoutRotation.sectionRotations,
                  SectionJourney.SECTION_ROTATION,
                )
              : lang.layoutRotation.sectionRotations,
            content: (
              <AssemblySectionRotationAreaContainer>
                <AssemblySectionRotationArea
                  previewButtonLabel={
                    getLayoutTitle
                      ? getLayoutTitle(
                          lang.layoutRotation
                            .sectionRotationsPreviewButtonLabel,
                          SectionJourney.SECTION_ROTATION,
                          true,
                        )
                      : lang.layoutRotation.sectionRotationsPreviewButtonLabel
                  }
                  inputLabel={lang.layoutRotation.sectionRotationsInputLabel}
                  inputTooltip={getTooltipValue('sectionRotationsInputTooltip')}
                  preview={
                    activeAssemblyJourney === SectionJourney.SECTION_ROTATION
                  }
                  onPreviewClick={() =>
                    setActiveAssemblyJourney(SectionJourney.SECTION_ROTATION)
                  }
                  inputValue={sectionRotationValue}
                  onInputChange={(value) =>
                    onUpdateRotationInterval &&
                    onUpdateRotationInterval(value, 'sectionRotation')
                  }
                  disabled={disabled}
                  error={!isValidDuration(sectionRotationValue)}
                  helperText={
                    !isValidDuration(sectionRotationValue)
                      ? lang.layoutRotation.maxTimeErrorMessage
                      : ''
                  }
                />
              </AssemblySectionRotationAreaContainer>
            ),
            disabledExpand: false,
            hasError: false,
            defaultExpanded: false,
          },
        ]
      : []),
    ...(hasTakeOver
      ? [
          {
            label: getLayoutTitle
              ? getLayoutTitle(
                  lang.layoutRotation.takeover,
                  SectionJourney.TAKE_OVER,
                )
              : lang.layoutRotation.takeover,
            content: (
              <AssemblySectionRotationAreaContainer>
                <AssemblySectionRotationArea
                  previewButtonLabel={
                    getLayoutTitle
                      ? getLayoutTitle(
                          lang.layoutRotation.takeoverPreviewButtonLabel,
                          SectionJourney.TAKE_OVER,
                          true,
                        )
                      : lang.layoutRotation.takeoverPreviewButtonLabel
                  }
                  inputLabel={lang.layoutRotation.takeoverInputLabel}
                  inputTooltip={getTooltipValue('takeoverInputTooltip')}
                  preview={activeAssemblyJourney === SectionJourney.TAKE_OVER}
                  onPreviewClick={() =>
                    setActiveAssemblyJourney(SectionJourney.TAKE_OVER)
                  }
                  inputValue={takeoverValue}
                  onInputChange={(value) =>
                    onUpdateRotationInterval &&
                    onUpdateRotationInterval(value, 'takeover')
                  }
                  disabled={disabled}
                  error={!isValidDuration(takeoverValue)}
                  helperText={
                    !isValidDuration(takeoverValue)
                      ? lang.layoutRotation.maxTimeErrorMessage
                      : ''
                  }
                />
              </AssemblySectionRotationAreaContainer>
            ),
            disabledExpand: false,
            hasError: false,
            defaultExpanded: false,
          },
        ]
      : []),
  ];
};
